import React, { useEffect, useState, useRef } from 'react';
import { get, post, put } from 'helpers/api';
import axios from 'axios';

import LoadingSpinner from 'components/ui/loading_spinner';
import Pagination from 'components/ui/pagination';
import { showSnackbar } from 'components/ui/snackbar/helper';

import Modal from 'components/modal';
import PropTypes from 'prop-types';
import ReportTable from './table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUndo } from '@fortawesome/pro-solid-svg-icons';
import ReportFilters from './filters';
import AllFiltersModal from './all_filters_modal';
import SavedReportModal from './saved_report_modal';
import { CHILD_FILTER_TYPE, filterTypeOptions } from './util';
import { Button } from 'components/ui/button';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import humanizeString from 'humanize-string';
import { v4 as uuid } from 'uuid';
import { getCookieValue } from 'helpers/cookie_helpers';
import ColumnselectModal from './column_select';
import ReportHeaderActions, { GENERATING_CSV_STEPS } from './header_actions';
import snakecase from 'lodash/snakeCase';
import ReportBreadCrumbs from './breadcrumbs';

const pageSize = 25;

function setupSelectedFilters(filters, applyDefaultValue = true) {
  return Object.keys(filters).reduce((acc, filterKey) => {
    const filter = filters[filterKey];
    const filterType = filter.type;
    const filterStartingValue = {
      [filterKey]: applyDefaultValue && filter.defaultValue
        ? filterTypeOptions[filterType].setupFilters(filter.defaultValue)
        : filterTypeOptions[filterType].setupFilters()
    };
    if (filter.childFilter) {
      filterStartingValue[filter.childFilter.key] = filterTypeOptions[CHILD_FILTER_TYPE].setupFilters();
    }

    return Object.assign(acc, filterStartingValue);
  }, {});
}

function setupSelectedColumns(savedReport, columns) {
  if (savedReport) {
    const selectedColumns = savedReport.selectedColumns;
    if (selectedColumns.length === 0) return defaultColumns(columns);

    return selectedColumns.map((selectedColumnName) => {
      return columns.find(column => column.name === selectedColumnName);
    }).filter(Boolean); // filters out undefined values
  }
  return defaultColumns(columns);
}

function defaultColumns(columns) {
  return columns.filter(column => column.default);
}

function setupBaseReport(savedReport, columns, filters, name, reportClass) {
  const reportFilters = setupSelectedFilters(filters);
  const sortableColumn = columns.find(column => column.sortable);
  const reportSorts = {
    column: sortableColumn ? sortableColumn.key : '',
    direction: 'asc'
  };

  if (savedReport) {
    return {
      ...savedReport,
      filters: { ...reportFilters, ...savedReport.filters },
      sorts: savedReport.sorts || reportSorts
    };
  } else {
    return {
      filters: reportFilters,
      name: name,
      report_class: reportClass,
      shared: 'false',
      sorts: reportSorts
    };
  }
}

export const PAGE_PATHS = {
  DEFAULT: 'default',
  GENERATE_CSV: 'generate_csv',
  GENERATE_PDF: 'generate_pdf',
  GENERATING_PDF: 'generating_pdf',
};

export default function BaseReport({
  allFilterOrder,
  baseReportName,
  columns,
  csvDownloadPath,
  defaultGroupingOptions,
  disablePdf = false,
  filters,
  filterValues,
  itemSingularLabel,
  reportClass,
  reportPath,
  reportsPath,
  reportType,
  savedReport,
  savedReportsUrl,
  searchTextRansackLabel,
  timeZone,
  transformRecords,
  updatedSavedReportUrl,
  url,
  onSetSavedReport
}) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ page: 1, pageSize });
  const [selectedRecordIds, setSelectedRecordIds] = useState([]);

  const [report, setReport] = useState(setupBaseReport(savedReport, columns, filters, baseReportName, reportClass));

  const [pagePath, setPagePath] = useState(PAGE_PATHS.DEFAULT);

  const [showAllFiltersModal, setShowAllFiltersModal] = useState(false);
  const [generatingCSVStep, setGeneratingCSVStep] = useState(GENERATING_CSV_STEPS.DEFAULT);
  const [showSaveModal, setShowModal] = useState(false);

  const [generatePdfOptions, setGeneratePdfOptions] = useState({ sort_by: '', group_by: '' });
  const [editColumnsModalOpen, setEditColumnsModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(setupSelectedColumns(savedReport, columns));
  const [allSelected, setAllSelected] = useState(false);
  const [primaryBrokerOnly, setPrimaryBrokerOnly] = useState(true);

  const generatingPdf = pagePath === PAGE_PATHS.GENERATING_PDF;
  const fetchRecordsId = useRef();
  const downloadKey = useRef(uuid());
  const downloadIntervalId = useRef();
  const abortControllerRef = useRef();

  const loadingPdfText = 'Gathering all matching properties for your PDF, this could take a while...';

  useEffect(() => {
    fetchRecords();
  }, []);

  // Backfill the export descriptor for reports saved before email scheduling existed, so
  // list-page "Schedule email" works without an explicit re-save (it can't translate filters itself).
  useEffect(() => {
    if (!savedReport || savedReport.exportQuery) return;

    let exportQuery;
    try {
      exportQuery = buildExportQuery();
    } catch {
      return;
    }

    // Fire-and-forget: only persist export_query to the DB; the update response is a different
    // serializer shape than this prop, so don't swap it into local state.
    put(updatedSavedReportUrl, { exportQuery }).catch(() => undefined);
  }, []);

  const firstPrimaryBrokerRender = useRef(true);
  useEffect(() => {
    if (firstPrimaryBrokerRender.current) {
      firstPrimaryBrokerRender.current = false;
      return;
    }
    debouncedFetchRecords({ currentPage: 1 });
  }, [primaryBrokerOnly]);

  const togglePrimaryBrokerFilter = () => {
    setPrimaryBrokerOnly(!primaryBrokerOnly);
  };

  const behaviorToggle = () => {
    if (reportType !== 'property') return null;

    return {
      value: 'togglePrimaryBrokerFilter',
      label: 'Primary Broker Only',
      checked: primaryBrokerOnly,
      onChange: togglePrimaryBrokerFilter,
      optionToToggle: 'broker'
    };
  };

  const reportParams = async () => {
    const fieldsToSend = selectedColumns.map(column => snakecase(column.value));
    const propertyIds = allSelected ? await fetchAllMatchingProperties() : selectedRecordIds;

    // TODO: remove explicit checks of reportType
    if (reportType === 'property') {
      return {
        inventoryReport: {
          fields: fieldsToSend,
          groupBy: generatePdfOptions['group_by']['value'],
          propertyIds: propertyIds,
          sortBy: generatePdfOptions['sort_by']['value']
        }
      };
    }

    return {
      researchCompReport: {
        compAttributes: fieldsToSend,
        comp_ids: selectedRecordIds,
        docsDocumentTemplateId: null,
        name: null
      },
      type: reportType === 'leaseComp' ? 'lease' : 'sale',
      newReport: true
    };
  };

  function generatePdfButtonText() {
    if (generatingPdf) {
      return 'Generating PDF...';
    } else if (pagePath === PAGE_PATHS.GENERATE_PDF) {
      return 'Download PDF';
    }

    return 'Save';
  }

  useEffect(() => {
    if (generatingPdf) {
      const generatePdf = async () => {
        try {
          const params = await reportParams();
          const data = await post(reportPath, params);
          window.location.assign(data.editPath);
        } catch (error) {
          alert('There was an error generating the PDF. Please try again.');
          setPagePath(PAGE_PATHS.DEFAULT);
        }
      };

      generatePdf();
    }
  }, [selectedColumns]);

  function generatePdf(newColumns) {
    setSelectedColumns(newColumns);
    setPagePath(PAGE_PATHS.GENERATING_PDF);
  }

  function saveSelectedColumns(newColumns) {
    setSelectedColumns(newColumns);
    setEditColumnsModalOpen(false);
  }

  const isSelected = record => selectedRecordIds.includes(record.uniqueId || record.id);

  function ransackParams(updatedValues = {}) {
    const valuesToUse = {
      selectedFilterValues: report.filters,
      currentPage: meta.currentPage,
      sortColumn: report.sorts.column,
      sortOrder: report.sorts.direction,
      ...updatedValues
    };
    const sortColumn = columns.find(column => column.key === valuesToUse.sortColumn).ransackLabel;
    const startingObject = {
      ['q[s]']: `${sortColumn} ${valuesToUse.sortOrder}`,
      [`q[${searchTextRansackLabel}]`]: valuesToUse.selectedFilterValues.search ?? ''
    };
    if (timeZone) {
      startingObject.time_zone = timeZone;
    }
    if (reportType !== 'user') {
      startingObject['q[property_draft_eq]'] = false;
    }

    return Object.keys(filters).reduce((acc, filterKey) => {
      const filterData = filters[filterKey];
      const valuesForKey = valuesToUse.selectedFilterValues[filterKey];
      const filterType = filterData.type;
      const paramsForFilter = filterTypeOptions[filterType].ransackParams(filters, filterKey, valuesForKey);
      if (filterKey === 'broker' && valuesForKey.length > 0 && primaryBrokerOnly) {
        Object.assign(acc, { 'q[property_projects_users_primary_eq]': true });
      }

      if (filterData.childFilter) {
        const childFilterValue = valuesToUse.selectedFilterValues[filterData.childFilter.key];
        const childRansack = filterTypeOptions[CHILD_FILTER_TYPE].ransackParams(filters, filterKey, childFilterValue);
        Object.assign(paramsForFilter, childRansack);
      }

      return Object.assign(acc, paramsForFilter);
    }, startingObject);
  }

  function debouncedFetchRecords(updatedValues = {}) {
    clearTimeout(fetchRecordsId.current);
    setLoading(true);

    fetchRecordsId.current = setTimeout(() => fetchRecords(updatedValues), 250);
  }

  async function fetchAllMatchingProperties() {
    setLoading(true);
    setEditColumnsModalOpen(false);
    const params = { paginate: false, ...ransackParams() };

    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const response = await get(url, { params, signal: abortControllerRef.current.signal });
      const propertyIds = transformRecords(response).map(record => record.uniqueId || record.id);

      return propertyIds;
    } catch (error) {
      if (!axios.isCancel(error)) {
        setLoading(false);
      }
      showErrorMessage(`Error fetching records: ${error.message}`);
    }
  }

  async function fetchRecords(updatedValues = {}) {
    setLoading(true);
    const params = {
      page: updatedValues.currentPage || meta.currentPage,
      limit: pageSize,
      ...ransackParams(updatedValues)
    };

    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const response = await get(url, { params, signal: abortControllerRef.current.signal });
      const transformedRecords = transformRecords(response);
      setRecords(transformedRecords);
      setMeta(response.meta);
      if (allSelected) {
        setSelectedRecordIds(transformedRecords.map(record => record.uniqueId || record.id));
      }
      setLoading(false);
    } catch (error) {
      if (!axios.isCancel(error)) {
        setLoading(false);
      }
      showErrorMessage(`Error fetching records: ${error.message}`);
    }
  }

  const handleSelectRecord = (record) => {
    const recordId = record.uniqueId || record.id;

    if (isSelected(record)) {
      const newList = selectedRecordIds.filter(id => id !== recordId);
      setSelectedRecordIds(newList);
    } else {
      setSelectedRecordIds(selectedRecordIds => [...selectedRecordIds, recordId]);
    }
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedRecordIds([]);
      setAllSelected(false);
    } else {
      setSelectedRecordIds(records.map(record => record.uniqueId || record.id));
      setAllSelected(true);
    }
  };

  const handleSortUpdate = (column) => {
    let newSortOrder = 'asc';
    if (report.sorts.column === column) newSortOrder = report.sorts.direction === 'asc' ? 'desc' : 'asc';

    setReport({
      ...report,
      sorts: {
        column: column,
        direction: newSortOrder
      }
    });
    setMeta({ ...meta, currentPage: 1 });
    debouncedFetchRecords({ currentPage: 1, sortColumn: column, sortOrder: newSortOrder });
  };

  function handlePageChange(e) {
    setMeta({ ...meta, currentPage: e });
    debouncedFetchRecords({ currentPage: e });
  }

  function updateFilters(updatedFilters) {
    setReport({
      ...report,
      filters: updatedFilters
    });
    debouncedFetchRecords({ selectedFilterValues: updatedFilters });
  }

  function handleFilterChange(event, filterKey, nameOfFilter) {
    const filterObject = filters[nameOfFilter];
    const filterType = filterObject.type;

    let childObject;

    if (filterType === 'accordion' && filterObject.childLabel) {
      childObject = {
        label: filterObject.childLabel,
        values: filterValues[filterObject.childLabel].options.flat()
          .filter(option => option.parentValue === event.currentTarget.value)
      };
    }

    const updatedFilters = filterTypeOptions[filterType]
      .handleFilterChange(event, report.filters, nameOfFilter, filterKey, childObject);
    updateFilters(updatedFilters);
  }

  function removeFilter(nameOfFilter, filterKey, isChild = false) {
    const filterData = filters[nameOfFilter];
    let filterType = filterData.type;
    if (isChild) filterType = CHILD_FILTER_TYPE;

    let childLabel;
    if (filterType === 'accordion' && filterValues[nameOfFilter].childLabel) {
      childLabel = filterValues[nameOfFilter].childLabel;
    }

    const dataToPass = { allFilters: report.filters, nameOfFilter, filterKey, childLabel, filterData };
    const updatedFilters = filterTypeOptions[filterType].removeFilter(dataToPass);
    updateFilters(updatedFilters);
  }

  function clearFilters() {
    const updatedFilters = setupSelectedFilters(filters, false);
    updateFilters(updatedFilters);
  }

  function updatedFilterObject() {
    return Object.keys(filters).reduce((acc, key) => {
      acc[key] = {
        ...filters[key],
        ...filterValues[key],
        handleChange: (event, abc) => handleFilterChange(event, abc, key),
        filterKey: key,
      };

      return acc;
    }, {});
  }

  function searchChanged(event) {
    const updatedText = event.currentTarget.value;
    const updatedReportFilters = {
      ...report.filters,
      search: updatedText
    };
    setReport({
      ...report,
      filters: updatedReportFilters
    });
    debouncedFetchRecords({ selectedFilterValues: updatedReportFilters });
  }

  function pageData() {
    if (loading) return {};

    return {
      currentPage: meta.currentPage,
      itemCount: Math.min(meta.totalCount, pageSize),
      itemSingularLabel: itemSingularLabel || humanizeString(reportType),
      pageSize,
      propertySelectedCount: allSelected ? meta.totalCount : selectedRecordIds.length,
      totalCount: meta.totalCount
    };
  }

  function changePagePath(newPagePath) {
    setPagePath(newPagePath);
    if (newPagePath === PAGE_PATHS.GENERATE_PDF) {
      setEditColumnsModalOpen(true);
    } else if (newPagePath === PAGE_PATHS.GENERATE_CSV) {
      onGenerateCSV();
    }
  }

  function renderResults() {
    if (records.length > 0) {
      return (
        <React.Fragment>
          <div className="table-container border rounded overflow-auto">
            <ReportTable
              allSelected={allSelected}
              columns={selectedColumns}
              isSelected={isSelected}
              records={records}
              reportType={reportType}
              selectedRecordIds={selectedRecordIds}
              sortColumn={report.sorts.column}
              sortOrder={report.sorts.direction}
              onRecordSelected={handleSelectRecord}
              onSelectAll={handleSelectAll}
              onSortUpdate={handleSortUpdate}
            />
          </div>
          <Pagination currentPage={meta.currentPage} totalPages={meta.totalPages} onChange={handlePageChange}/>
        </React.Fragment>
      );
    }

    return (
      <Empty className="bg-white" style={{ minHeight: '50vh' }}>
        <Empty.Media>
          <FontAwesomeIcon icon={faSearch} />
        </Empty.Media>
        <Empty.Title>No results found</Empty.Title>
        <Empty.Content>Search with different criteria or reset filters</Empty.Content>
        <Empty.Actions>
          <Button icon={faUndo} variant="ghost" onClick={clearFilters}>
            Reset Filters
          </Button>
        </Empty.Actions>
      </Empty>
    );
  }

  function onGenerateCSV() {
    setGeneratingCSVStep(GENERATING_CSV_STEPS.GENERATING);
    setPagePath(PAGE_PATHS.DEFAULT);
    clearInterval(downloadIntervalId.current);
    downloadIntervalId.current = setInterval(checkDownloadComplete, 250);
  }

  function checkDownloadComplete() {
    if (getCookieValue('download_key') === downloadKey.current) {
      clearInterval(downloadIntervalId.current);
      setGeneratingCSVStep(GENERATING_CSV_STEPS.GENERATED);
      setTimeout(() => setGeneratingCSVStep(GENERATING_CSV_STEPS.DEFAULT), 1500);

      const error = getCookieValue(`${downloadKey.current}_error`);
      if (error) alert(error.replace(new RegExp('\\+', 'g'), ' '));
    }
  }

  const columnParams = () => {
    return selectedColumns.map(column => column.name);
  };

  // Captures the resolved CSV export request so a scheduled email job can regenerate
  // this report headlessly (the filter -> ransack translation only exists here in JS).
  // `kind` selects the matching server-side CSV backend in Reports::BuildReportCsv.
  const buildExportQuery = () => {
    const q = {};
    Object.entries(ransackParams()).forEach(([key, value]) => {
      const match = key.match(/^q\[(.+)\]$/);
      if (match) q[match[1]] = value;
    });

    const kindByReportType = {
      property: 'inventory',
      leaseComp: 'lease_comp',
      saleComp: 'sale_comp',
      user: 'user'
    };

    return {
      kind: kindByReportType[reportType],
      reportClass,
      columns: selectedColumns.map(column => column.label),
      timeZone,
      q
    };
  };

  async function updateReport() {
    const params = {
      ...report,
      name: baseReportName,
      selectedColumns: columnParams(),
      exportQuery: buildExportQuery()
    };

    try {
      const response = await put(updatedSavedReportUrl, params);
      onSetSavedReport(response);
      updateUIAfterSave(response);
    } catch (error) {
      showFailedUpdateUI(error);
    } finally {
      setShowModal(false);
    }
  }

  async function saveReportAs(inventoryName) {
    const params = {
      ...report,
      name: inventoryName,
      selectedColumns: columnParams(),
      exportQuery: buildExportQuery()
    };

    let response;
    try {
      response = await post(savedReportsUrl, params);
      onSetSavedReport(response);
      updateUIAfterSave(response);
    } catch (error) {
      showFailedUpdateUI();
    } finally {
      setShowModal(false);
    }
  }

  const updateUIAfterSave = (response) => {
    showSnackbar({ text: 'Report saved successfully!', variant: 'success' });

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('saved_report_id', response.id);
    window.history.pushState({}, '', currentUrl);
  };

  const showFailedUpdateUI = () => {
    showSnackbar({ text: 'Report did not save successfully', variant: 'danger' });
  };

  const showErrorMessage = (message) => {
    showSnackbar({ text: message, variant: 'danger' });
  };

  const closeColumnSelectModal = () => {
    setPagePath(PAGE_PATHS.DEFAULT);
    setEditColumnsModalOpen(false);
  };

  return (
    <React.Fragment>
      <ReportBreadCrumbs baseReportName={baseReportName} reportsPath={reportsPath} savedReport={savedReport} />

      <div className="container-fluid d-flex flex-column w-100 min-h-0">
        { reportType !== 'user' && (
          <AllFiltersModal
            allFilterOrder={allFilterOrder}
            filterMapping={updatedFilterObject()}
            show={showAllFiltersModal}
            startingFilters={report.filters}
            onChangeFilters={(updatedFilters) => {
              debouncedFetchRecords({ selectedFilterValues: updatedFilters });
              setReport({
                ...report,
                filters: updatedFilters
              });
            }}
            onClearFilters={clearFilters}
            onCloseModal={() => setShowAllFiltersModal(false)}
          />
        )}
        <SavedReportModal
          closeModal={() => {
            setShowModal(false);
          }}
          saveReportName={saveReportAs}
          selectedFilterValues={report.filters}
          show={(showSaveModal)}
        />
        <Modal className="multiselect-modal" show={editColumnsModalOpen} size="lg" onHide={closeColumnSelectModal}>
          <ColumnselectModal
            defaultGroupingOptions={defaultGroupingOptions}
            generatePdfOptions={generatePdfOptions}
            generateType={pagePath}
            initiallySelected={selectedColumns}
            key={editColumnsModalOpen}
            optionLabelName="name"
            options={columns}
            optionsLabel="Columns"
            optionValueName="name"
            reorderable={true}
            saveButtonLabel={generatePdfButtonText()}
            saving={generatingPdf}
            setGeneratedPdfOptions={setGeneratePdfOptions}
            title="Edit Columns"
            onClose={closeColumnSelectModal}
            onSave={pagePath === PAGE_PATHS.GENERATE_PDF ? generatePdf : saveSelectedColumns}
          />
        </Modal>
        <div className="d-flex align-items-center justify-content-between my-3">
          <div>
            <h5 className="m-0">{savedReport?.name || baseReportName}</h5>
            {timeZone && <div className="text-muted small">Timezone: {timeZone}</div>}
          </div>
          <ReportHeaderActions
            allSelected={allSelected}
            csvDownloadPath={csvDownloadPath}
            disablePdf={disablePdf}
            downloadKey={downloadKey}
            generatingCSVStep={generatingCSVStep}
            ransackParams={ransackParams}
            savedReport={savedReport}
            selectedColumns={selectedColumns}
            selectedRecordIds={selectedRecordIds}
            timeZone={timeZone}
            onEditColumns={() => setEditColumnsModalOpen(true)}
            onPathChanged={changePagePath}
            onSaveAs={() => setShowModal(true)}
            onUpdateReport={updateReport}
          />
        </div>
        <ReportFilters
          behaviorToggle={behaviorToggle()}
          filterMapping={updatedFilterObject()}
          filters={report.filters}
          pageData={pageData()}
          reportType={reportType}
          searchText={report.filters.search || ''}
          onClearFilters={clearFilters}
          onRemoveFilter={removeFilter}
          onSearchChange={searchChanged}
          onToggleAllFiltersModal={() => setShowAllFiltersModal(true)}
        />
        {loading ? <LoadingSpinner loadingText={generatingPdf ? loadingPdfText : null}/> : renderResults()}
      </div>
    </React.Fragment>
  );
}

BaseReport.propTypes = {
  allFilterOrder: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        icon: PropTypes.object,
        accordionIcons: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string.isRequired,
            icon: PropTypes.object.isRequired,
          })
        ),
        minPlaceholder: PropTypes.string,
        maxPlaceholder: PropTypes.string,
      })
    )
  ).isRequired,
  baseReportName: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    default: PropTypes.bool.isRequired,
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    locked: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    sortable: PropTypes.bool,
    ransackLabel: PropTypes.string,
  })).isRequired,
  csvDownloadPath: PropTypes.string.isRequired,
  defaultGroupingOptions: PropTypes.object.isRequired,
  disablePdf: PropTypes.bool,
  filters: PropTypes.object.isRequired,
  filterValues: PropTypes.object.isRequired,
  itemSingularLabel: PropTypes.string,
  reportClass: PropTypes.string.isRequired,
  reportPath: PropTypes.string,
  reportsPath: PropTypes.string.isRequired,
  reportType: PropTypes.oneOf(['property', 'leaseComp', 'saleComp', 'user']),
  savedReport: PropTypes.object,
  savedReportsUrl: PropTypes.string.isRequired,
  searchTextRansackLabel: PropTypes.string.isRequired,
  timeZone: PropTypes.string,
  transformRecords: PropTypes.func.isRequired,
  updatedSavedReportUrl: PropTypes.string,
  url: PropTypes.string.isRequired,
  onSetSavedReport: PropTypes.func.isRequired,
};
